/**
 * Clinical intake — verified against cms-backend src/intake at 5379fe9
 * (Intake redesign, ZOY-569/570/571/572 → cms-front ZOY-574); attachments
 * against b9a47c3 (ZOY-704 → ZOY-705).
 *
 * Never gates anything; attaches to the record, not an appointment;
 * completion is an explicit act that does NOT freeze content (every later
 * edit is ledgered server-side); self-reported provenance per row.
 *
 * Contract rules the client must honour, not re-invent:
 *  - `null` clears a scalar / marks a three-state boolean unanswered; omit
 *    to leave it untouched.
 *  - DOB and referral source are PATIENT-owned: read them from `patient`,
 *    never send them here (400).
 *  - Complaints and medications pick from catalogues; the pre-redesign
 *    free-text bodies are refused. Legacy rows still read back as stored.
 *  - Approximate medication dates are an ISO anchor (first day of the
 *    stated period in the clinic's calendar) + a `precision`, never a day.
 */

import { apiRequest } from '../api/http';
import type { IntakeSummary } from './patientsApi';

export const CAPTURE_MODES = ['self_entry', 'staff_assisted'] as const;
export type CaptureMode = (typeof CAPTURE_MODES)[number];

// ── vocabularies (ZOY-569/571/572; slugs are the wire, labels are UI) ─────────

export const NICU_DURATION_UNITS = ['days', 'weeks', 'months'] as const;
export type NicuDurationUnit = (typeof NICU_DURATION_UNITS)[number];

/** Fixed scale on NEW writes; a legacy row may carry a free string. */
export const COMPLAINT_SEVERITIES = ['mild', 'moderate', 'severe'] as const;
export type ComplaintSeverity = (typeof COMPLAINT_SEVERITIES)[number];
export const isComplaintSeverity = (v: string | null): v is ComplaintSeverity =>
  v !== null && (COMPLAINT_SEVERITIES as readonly string[]).includes(v);

/** Required on new writes; a legacy row reads `null` = unspecified — never assume current. */
export const MEDICATION_TIMEFRAMES = ['current', 'historical'] as const;
export type MedicationTimeframe = (typeof MEDICATION_TIMEFRAMES)[number];

export const DATE_PRECISIONS = ['year', 'month'] as const;
export type DatePrecision = (typeof DATE_PRECISIONS)[number];

export const HISTORY_SECTIONS = ['medical_condition', 'developmental_history'] as const;
export type HistorySection = (typeof HISTORY_SECTIONS)[number];

/** Baseline complaint categories; a clinic-added complaint may carry another slug. */
export const COMPLAINT_CATEGORIES = [
  'speech_language',
  'social_communication',
  'behaviour_attention',
  'motor',
  'sensory',
  'feeding_sleep',
  'learning',
  'other',
] as const;

// ── rows ─────────────────────────────────────────────────────────────────────

export interface ComplaintRow {
  id: string;
  /** Legacy free-text row (`complaintItem: null`) or the Other item's text; null on a catalogue pick. */
  description: string | null;
  /** One of COMPLAINT_SEVERITIES on new rows; a legacy row may hold any string. */
  severity: string | null;
  duration: string | null;
  notes: string | null;
  selfReported: boolean;
  createdBy: string;
  /** Resolved live; null = legacy row that predates the catalogue. */
  complaintItem: { id: string; name: string; category: string; isOther: boolean } | null;
}

export interface HistorySelectionRow {
  id: string;
  detail: string | null;
  selfReported: boolean;
  createdBy: string;
  /** `section: null` = unclassified legacy item — belongs to neither redesigned section. */
  historyItem: {
    id: string;
    name: string;
    category: string | null;
    section: string | null;
    isOther: boolean;
  };
}

export interface IntakeMedicationRow {
  id: string;
  /** null on legacy rows = unspecified. */
  timeframe: MedicationTimeframe | null;
  /** DEPRECATED — legacy rows only; read-only, never sent back. */
  status: string | null;
  /** Free text when the master list lacked it; the row's identity together with `medication`. */
  otherName: string | null;
  medication: {
    id: string;
    name: string;
    genericName: string | null;
    category: string | null;
  } | null;
  dosage: string | null;
  frequency: string | null;
  reason: string | null;
  prescriber: string | null;
  /** ISO 'YYYY-MM-DD' anchor + precision, or both null. */
  startOn: string | null;
  startPrecision: DatePrecision | null;
  endOn: string | null;
  endPrecision: DatePrecision | null;
  notes: string | null;
  selfReported: boolean;
  createdBy: string;
}

/** The twelve ZOY-569 perinatal / clinical-report scalars. */
export interface IntakeClinicalFields {
  apgarScore: string | null;
  /** Three-state: null = unanswered, which is NOT "no". */
  nicuAdmitted: boolean | null;
  /** Both or neither — INTAKE_NICU_DURATION_INCOMPLETE otherwise (merged state). */
  nicuDurationValue: number | null;
  nicuDurationUnit: NicuDurationUnit | null;
  nicuDetails: string | null;
  jaundice: boolean | null;
  jaundiceDetails: string | null;
  eegReport: string | null;
  brainImagingReport: string | null;
  metabolicLabReport: string | null;
  brainInjuryHistory: string | null;
  surgeryHistory: string | null;
}

export interface IntakeDetail extends IntakeSummary, IntakeClinicalFields {
  contextGoals: string | null;
  notes: string | null;
  /** ZOY-487: finalized recordings on this record — travels with the read. */
  recordingCount?: number;
  /** Patient-owned, read-only here (ZOY-569). */
  patient: { dateOfBirth: string | null; dobEstimated: boolean; referralSource: string | null };
  complaints: ComplaintRow[];
  historySelections: HistorySelectionRow[];
  medications: IntakeMedicationRow[];
  /** ZOY-704: live file attachments (completed, not removed), oldest first — facts only, never a URL. */
  attachments: IntakeAttachment[];
}

export const IntakeErrorCode = {
  AlreadyCompleted: 'INTAKE_ALREADY_COMPLETED',
  EntryAlreadyRemoved: 'INTAKE_ENTRY_ALREADY_REMOVED',
  EntryNotFound: 'INTAKE_ENTRY_NOT_FOUND',
  HistoryAlreadySelected: 'INTAKE_HISTORY_ALREADY_SELECTED',
  /** An Other item needs its free text, on add and on amend. */
  OtherDetailRequired: 'INTAKE_OTHER_DETAIL_REQUIRED',
  /** `description` on a catalogue complaint. */
  DescriptionNotAllowed: 'INTAKE_DESCRIPTION_NOT_ALLOWED',
  NicuDurationIncomplete: 'INTAKE_NICU_DURATION_INCOMPLETE',
  MedicationDateInvalid: 'INTAKE_MEDICATION_DATE_INVALID',
  MedicationEndNotAllowed: 'INTAKE_MEDICATION_END_NOT_ALLOWED',
  MedicationDateOrder: 'INTAKE_MEDICATION_DATE_ORDER',
  MedicationSourceInvalid: 'INTAKE_MEDICATION_SOURCE_INVALID',
  /** The picked catalogue row is archived / clinic-disabled — "no longer available", not a fault. */
  CatalogEntryDisabled: 'CATALOG_ENTRY_DISABLED',
} as const;

// ── attachments (ZOY-704 / ZOY-705) ──────────────────────────────────────────

/** Exact MIME match on both sides; the picker offers nothing else. */
export const INTAKE_ATTACHMENT_CONTENT_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
] as const;
export type IntakeAttachmentContentType = (typeof INTAKE_ATTACHMENT_CONTENT_TYPES)[number];
export const isIntakeAttachmentContentType = (v: string): v is IntakeAttachmentContentType =>
  (INTAKE_ATTACHMENT_CONTENT_TYPES as readonly string[]).includes(v);

/** 10 MiB per file — the server's INTAKE_ATTACHMENT_MAX_BYTES, refused locally first. */
export const INTAKE_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
/** Live attachments per intake — the server's INTAKE_ATTACHMENT_MAX_PER_INTAKE. */
export const INTAKE_ATTACHMENT_MAX_PER_INTAKE = 10;
export const INTAKE_ATTACHMENT_DESCRIPTION_MAX = 500;

export const INTAKE_ATTACHMENT_STATUSES = [
  'initiated',
  'completed',
  'abandoned',
  'rejected',
] as const;
export type IntakeAttachmentStatus = (typeof INTAKE_ATTACHMENT_STATUSES)[number];

/** The attachment view — never carries a storage URL (the download route mints one per click). */
export interface IntakeAttachment {
  id: string;
  status: IntakeAttachmentStatus | string;
  contentType: string;
  /** Verified byte count once completed. */
  sizeBytes: number | null;
  /** Display name only (the server keeps the last path segment). */
  filename: string | null;
  description: string | null;
  createdBy: string;
  createdAt: string;
  completedAt: string | null;
}

/** What a declaration hands back: the row plus where to PUT the bytes. */
export interface IntakeAttachmentTicket {
  attachment: IntakeAttachment;
  /** null on an idempotent replay whose bytes already exist — skip the PUT and complete. */
  uploadUrl: string | null;
  method: 'PUT';
  /** Headers the PUT must carry, exactly as cms-backend supplies them. */
  requiredHeaders: Record<string, string>;
  expiresAt: string | null;
}

export const IntakeAttachmentErrorCode = {
  NotFound: 'INTAKE_ATTACHMENT_NOT_FOUND',
  /** Ten live already — at declare, and again at complete. */
  LimitReached: 'INTAKE_ATTACHMENT_LIMIT_REACHED',
  /** The uploader refused the type, or the verified type is outside the allow-list. */
  ContentInvalid: 'INTAKE_ATTACHMENT_CONTENT_INVALID',
  /** Declared or verified size over the ceiling. */
  LimitExceeded: 'INTAKE_ATTACHMENT_LIMIT_EXCEEDED',
  /** Complete on a non-initiated row; describe/remove/download on a row that is not live. */
  InvalidState: 'INTAKE_ATTACHMENT_INVALID_STATE',
  /** Uploader unreachable — OR complete called before the bytes exist: retry after the PUT. */
  StorageUnavailable: 'INTAKE_ATTACHMENT_STORAGE_UNAVAILABLE',
} as const;

export interface DeclareIntakeAttachmentBody {
  contentType: IntakeAttachmentContentType;
  sizeBytes: number;
  filename?: string;
  description?: string;
  /** 8..128 chars; the same key replays the same row after a lost response. */
  idempotencyKey: string;
}

export const declareIntakeAttachment = (intakeId: string, body: DeclareIntakeAttachmentBody) =>
  apiRequest<IntakeAttachmentTicket>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/intakes/${intakeId}/attachments`,
    body,
  });

/** Idempotent; 503 STORAGE_UNAVAILABLE right after the PUT means "not yet" — retry. */
export const completeIntakeAttachment = (attachmentId: string) =>
  apiRequest<IntakeAttachment>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/intakes/attachments/${attachmentId}/complete`,
    body: {},
  });

/** `null` clears the description. */
export const patchIntakeAttachment = (attachmentId: string, body: { description: string | null }) =>
  apiRequest<IntakeAttachment>({
    base: 'clinic',
    method: 'PATCH',
    path: `/v1/intakes/attachments/${attachmentId}`,
    body,
  });

/** Soft withdrawal (ZOY-355): the row keeps removal attribution; the bytes go by a server job. */
export const removeIntakeAttachment = (attachmentId: string, reason?: string) =>
  apiRequest<{ id: string; removedAt: string; removedBy: string; removalReason: string | null }>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/intakes/attachments/${attachmentId}/remove`,
    body: reason ? { reason } : {},
  });

/** The ONLY route that returns a storage URL — short-lived; mint per click, never persist. */
export const intakeAttachmentDownloadUrl = (attachmentId: string) =>
  apiRequest<{ url: string; expiresAt: string }>({
    base: 'clinic',
    path: `/v1/intakes/attachments/${attachmentId}/download-url`,
  });

// ── intake ───────────────────────────────────────────────────────────────────

export type IntakeScalarPatch = Partial<
  { contextGoals: string | null; notes: string | null } & IntakeClinicalFields
>;

export const createIntake = (
  patientId: string,
  body: { captureMode: CaptureMode } & IntakeScalarPatch,
) =>
  apiRequest<IntakeSummary>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/patients/${patientId}/intakes`,
    body,
  });

export const fetchIntake = (id: string) =>
  apiRequest<IntakeDetail>({ base: 'clinic', path: `/v1/intakes/${id}` });

export const patchIntake = (id: string, body: IntakeScalarPatch) =>
  apiRequest<IntakeDetail>({ base: 'clinic', method: 'PATCH', path: `/v1/intakes/${id}`, body });

export const completeIntake = (id: string) =>
  apiRequest<IntakeSummary>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/intakes/${id}/complete`,
    body: {},
  });

// ── complaints (ZOY-571) ─────────────────────────────────────────────────────

export const addComplaint = (
  intakeId: string,
  body: {
    complaintItemId: string;
    /** ONLY for the Other item (required there); refused on a catalogue item. */
    description?: string;
    severity?: ComplaintSeverity | null;
    duration?: string | null;
    notes?: string | null;
    selfReported?: boolean;
  },
) =>
  apiRequest<ComplaintRow>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/intakes/${intakeId}/complaints`,
    body,
  });

export const patchComplaint = (
  complaintId: string,
  body: {
    /** Amendable only on an Other or legacy free-text row. */
    description?: string;
    severity?: ComplaintSeverity | null;
    duration?: string | null;
    notes?: string | null;
  },
) =>
  apiRequest<ComplaintRow>({
    base: 'clinic',
    method: 'PATCH',
    path: `/v1/intakes/complaints/${complaintId}`,
    body,
  });

/** Soft withdrawal (ZOY-355): the row keeps removal attribution; never a delete. */
export const removeComplaint = (complaintId: string, reason?: string) =>
  apiRequest<unknown>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/intakes/complaints/${complaintId}/remove`,
    body: reason ? { reason } : {},
  });

// ── history selections (ZOY-570) ─────────────────────────────────────────────

export const addHistorySelection = (
  intakeId: string,
  body: { historyItemId: string; detail?: string; selfReported?: boolean },
) =>
  apiRequest<HistorySelectionRow>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/intakes/${intakeId}/history`,
    body,
  });

export const patchHistorySelection = (selectionId: string, body: { detail?: string }) =>
  apiRequest<HistorySelectionRow>({
    base: 'clinic',
    method: 'PATCH',
    path: `/v1/intakes/history/${selectionId}`,
    body,
  });

/** Soft withdrawal (ZOY-355). */
export const removeHistorySelection = (selectionId: string, reason?: string) =>
  apiRequest<unknown>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/intakes/history/${selectionId}/remove`,
    body: reason ? { reason } : {},
  });

// ── medications (ZOY-572/573) ────────────────────────────────────────────────

export interface MedicationFacts {
  timeframe: MedicationTimeframe;
  dosage?: string | null;
  frequency?: string | null;
  reason?: string | null;
  prescriber?: string | null;
  startOn?: string | null;
  startPrecision?: DatePrecision | null;
  endOn?: string | null;
  endPrecision?: DatePrecision | null;
  notes?: string | null;
}

/** Exactly one of `medicationId` | `otherName` — the row's identity, never amendable. */
export type MedicationSource = { medicationId: string } | { otherName: string };

export const addIntakeMedication = (
  intakeId: string,
  body: MedicationSource & MedicationFacts & { selfReported?: boolean },
) =>
  apiRequest<IntakeMedicationRow>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/intakes/${intakeId}/medications`,
    body,
  });

/** Facts amend; the source never does (remove + re-add corrects a wrong pick). No `status`. */
export const patchIntakeMedication = (entryId: string, body: Partial<MedicationFacts>) =>
  apiRequest<IntakeMedicationRow>({
    base: 'clinic',
    method: 'PATCH',
    path: `/v1/intakes/medications/${entryId}`,
    body,
  });

/** Soft withdrawal (ZOY-355). */
export const removeIntakeMedication = (entryId: string, reason?: string) =>
  apiRequest<unknown>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/intakes/medications/${entryId}/remove`,
    body: reason ? { reason } : {},
  });
